import React, { createRef } from "react";
import { ToastContainer, toast, Slide } from "react-toastify";
import {
  getNodesInShortestPathOrder,
  resetGridVisited,
} from "../algorithms/general";

import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.css";
import { binaryTree, prims } from "../algorithms/mazes";
import GridRenderer from "./gridRenderer";
import ControlPanel from "./controlPanel";
import {
  algorithmTypeOptions,
  animationIntervalOptions,
  heuristicOptions,
  movementStyleOptions
} from "../algorithms/options";

class Visualizer extends React.Component {
  constructor(props) {
    super(props);
    this.animationFrameId = null;
    this.animationData = null;
    this.NODE_SIZE = this.calcNodeSize();
    this.rows = this.calcGridRows();
    this.cols = this.calcGridCols();
    this.state = {
      movingNode: null,
      isDrawingObstacle: false,
      isAnimating: false,
      animationInterval: animationIntervalOptions[1], // fast
      algorithmType: algorithmTypeOptions[1],
      heuristic: heuristicOptions[0],
      movementStyle: movementStyleOptions[1],
      rows: this.rows,  // used for grid resizing
      cols: this.cols,  // used for grid resizing
    };
    this.startPoint = { row: 3, col: 3 };
    this.finishPoint = { row: this.rows - 4, col: this.cols - 4 };
    this.grid = this.createInitialGrid();
  }

  componentDidMount() {
    window.addEventListener("resize", this.resizeWindow);
    this.genMazeRandomPrims();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.resizeWindow);
    this.stopAnimation();
  }

  shouldComponentUpdate(nextProps, nextState) {
    // return false;
    return true;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.state.rows !== prevState.rows || this.state.cols !== prevState.cols) {
      this.clearGrid();
      this.genMazeRandomPrims();
    }
  }

  calcNodeSize = () => {
    const SMALL = 576;
    return window.innerWidth < SMALL || window.innerHeight < SMALL ? 10 : 20;
  }

  calcGridCols = () => {
    const cols = Math.ceil(window.innerWidth / this.NODE_SIZE);
    return cols % 2 === 0 ? cols + 1 : cols;
  };

  calcGridRows = () => {
    const rows = Math.ceil((window.innerHeight - 50 - 100) / this.NODE_SIZE);
    return rows % 2 === 0 ? rows + 1 : rows;
  };

  resizeWindow = () => {
    const oldRows = this.rows;
    const oldCols = this.cols;
    this.NODE_SIZE = this.calcNodeSize();
    this.rows = this.calcGridRows();
    this.cols = this.calcGridCols();
    if (oldRows !== this.rows || oldCols !== this.cols) {
      this.finishPoint = { row: this.rows - 4, col: this.cols - 4 };
      this.grid = this.createInitialGrid();
      this.stopAnimation();
      this.clearGrid();
      this.setState({ rows: this.rows, cols: this.cols })
    }
  };

  handleMouseDown = (row, col) => {
    if (this.state.isAnimating){
      return;
    }

    const startPoint = this.startPoint;
    const finishPoint = this.finishPoint;
    if (row === startPoint["row"] && col === startPoint["col"]) {
      this.setState({ movingNode: "start" });
    } //
    else if (row === finishPoint["row"] && col === finishPoint["col"]) {
      this.setState({ movingNode: "finish" });
    } //
    else {
      this.setState({ isDrawingObstacle: true });
      this.toggleNodeObstacle(row, col);
    }
  };

  handleMouseEnter = (row, col) => {
    const node = this.grid[row][col];

    // ignore event if mouse is over a start or finish node
    if (node.type === "start" || node.type === "finish" || this.state.isAnimating){
      return;
    }

    // if drawing obstacle
    if (this.state.isDrawingObstacle) {
      this.toggleNodeObstacle(row, col);
    }
    // if moving some special node like start or finish
    // eslint-disable-next-line
    else if (this.state.movingNode != null) {
      if (node.type === "regular") {
        var oldNode = null;
        node.type = this.state.movingNode;
        node.ref.current.setState({ type: this.state.movingNode });
        if (node.type === "start") {
          oldNode = this.grid[this.startPoint["row"]][this.startPoint["col"]];
          this.startPoint = { row: row, col: col };
        } //
        else if (node.type === "finish") {
          oldNode = this.grid[this.finishPoint["row"]][this.finishPoint["col"]];
          this.finishPoint = { row: row, col: col };
        }
        oldNode.type = "regular";
        oldNode.ref.current.setState({ type: "regular" });
      }
    }
  };

  handleMouseUp = () => {
    this.setState({ isDrawingObstacle: false, movingNode: null });
  };

  handleIntervalChange = (selectedOption) => {
    this.setState({ animationInterval: selectedOption });
  };

  handleAlgorithmChange = (selectedOption) => {
    this.setState({ algorithmType: selectedOption });
  };

  handleHeuristicChange = (selectedOption) => {
    this.setState({ heuristic: selectedOption });
  };

  handleMovementStyleChange = (selectedOption) => {
    this.setState({ movementStyle: selectedOption });
  };

  clearGridAnimation = () => {
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const element = document.getElementById(`node-${row}-${col}`);
        element?.classList.remove("node-visited");
        element?.classList.remove("node-shortest-path");
      }
    }
  };

  //create and return object representing a node
  createNode = (col, row) => {
    const startPoint = this.startPoint;
    const finishPoint = this.finishPoint;
    var nodeType = "regular";
    if (row === startPoint["row"] && col === startPoint["col"]) {
      nodeType = "start";
    } else if (row === finishPoint["row"] && col === finishPoint["col"]) {
      nodeType = "finish";
    }
    return {
      col,
      row,
      type: nodeType,
      distance: Infinity,
      isVisited: false,
      parentNode: null,
      ref: createRef(),
    };
  };

  //create and return an initialized grid
  createInitialGrid = () => {
    const grid = [];
    for (let row = 0; row < this.rows; row++) {
      const currentRow = [];
      for (let col = 0; col < this.cols; col++) {
        currentRow.push(this.createNode(col, row));
      }
      grid.push(currentRow);
    }
    return grid;
  };

  clearGrid = () => {
    this.clearGridAnimation();
    this.clearGridObstacles();
    resetGridVisited(this.grid);
    this.updateGridByRef();
  };

  clearGridObstacles = () => {
    for (let row of this.grid) {
      for (let node of row) {
        if (node.type === "obstacle") {
          node.type = "regular";
          node.ref.current.clearObstacle();
        }
      }
    }
  };

  addObstaclesConditionally = (condition, updateRef = false) => {
    // make each regular node an obstacle if condition is met
    for (let row of this.grid) {
      for (let node of row) {
        if (node.type === "regular" && condition()) {
          node.type = "obstacle";
          if (updateRef) node.ref.current.setState({ type: "obstacle" });
        }
      }
    }
  };

  addRandomObstacles = () => {
    this.addObstaclesConditionally(() => Math.random() >= 0.8, true);
  };

  genMazeBinaryTree = () => {
    resetGridVisited(this.grid);
    this.clearGridAnimation();
    this.addObstaclesConditionally(() => true);
    binaryTree(this.grid);
    this.updateGridByRef();
  };

  genMazeRandomPrims = () => {
    resetGridVisited(this.grid);
    this.clearGridAnimation();
    this.addObstaclesConditionally(() => true);
    prims(this.grid, this.startPoint);
    this.updateGridByRef();
  };

  updateGridByRef = () => {
    // update entire grid through each node's ref
    for (let row of this.grid) {
      for (let node of row) {
        node.ref.current?.setState({ type: node.type });
      }
    }
  };

  animateAlgo(visitedNodesInOrder, nodesInShortestPathOrder) {
    // initialize animation state
    this.setState({ isAnimating: true });
    this.animationData = {
      phase: 'visit',                  // 'visit' or 'path'
      visited: visitedNodesInOrder,
      path: nodesInShortestPathOrder,
      visitedIndex: 0,
      pathIndex: 0,
      lastTimestamp: null,
      visitInterval: this.state.animationInterval.value,
      pathInterval: 25,
    };
    this.animationFrameId = requestAnimationFrame(this._step);
  }

  _step = (timestamp) => {
    const data = this.animationData;
    if (!data.lastTimestamp) data.lastTimestamp = timestamp;
  
    const delta = timestamp - data.lastTimestamp;
    
    // console.log(delta);
  
    if (data.phase === 'visit') {
      const stepsToProcess = Math.floor(delta / data.visitInterval);
      // console.log("steps2process", stepsToProcess);
      for (let i = 0; i < stepsToProcess && data.visitedIndex < data.visited.length; i++) {
        const { row, col } = data.visited[data.visitedIndex++];
        const element = document.getElementById(`node-${row}-${col}`)
        if (data.visitInterval < 10) {
          element.style.animationDuration = "0.3s";
        } else {
          element.style.animationDuration = "1.0s";
        }
        element?.classList.add('node-visited');
      }

      if (data.visitedIndex >= data.visited.length) {
        // Move to shortest-path phase
        if (data.path.length === 0) {
          toast(
            <div className="d-flex align-items-center gap-2">
              <div className="node-finish d-inline-block" style={{ width: "0.8em", height: "0.8em"}}/>
              <span>No path found.</span>
            </div>
          );
          this.setState({ isAnimating: false });
          return;
        }
        data.phase = 'path';
        data.lastTimestamp = timestamp;
      } else {
        data.lastTimestamp += stepsToProcess * data.visitInterval;
      }
    }
  
    if (data.phase === 'path') {
      const stepsToProcess = Math.floor((timestamp - data.lastTimestamp) / data.pathInterval);
      for (let i = 0; i < stepsToProcess && data.pathIndex < data.path.length; i++) {
        const { row, col } = data.path[data.pathIndex++];
        document.getElementById(`node-${row}-${col}`)?.classList.add('node-shortest-path');
      }
  
      if (data.pathIndex >= data.path.length) {
        this.setState({ isAnimating: false });
        toast(
          <div style={{lineHeight: "normal"}}>
            <div className="d-flex align-items-center gap-1">
              <div className="node-shortest-path d-inline-block" style={{ width: "0.8em", height: "0.8em"}}/>
              <span>Found path of <b>{data.path.length}</b> steps.</span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <div className="node-visited d-inline-block" style={{ width: "0.8em", height: "0.8em"}}/>
              <span>Explored <b>{data.visited.length}</b> nodes in the grid.</span>
            </div>
          </div>
        );
        return;
      } else {
        data.lastTimestamp += stepsToProcess * data.pathInterval;
      }
    }
  
    if (this.state.isAnimating) {
      this.animationFrameId = requestAnimationFrame(this._step);
    }
  }

  stopAnimation = () => {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.setState({ isAnimating: false });
  }

  visualizeAlgo = () => {
    const sP = this.startPoint;
    const fP = this.finishPoint;
    this.clearGridAnimation();
    this.stopAnimation();
    const startNode = this.grid[sP.row][sP.col];
    const finishNode = this.grid[fP.row][fP.col];
    resetGridVisited(this.grid);
    const algorithm = this.state.algorithmType.value;
    const heuristic = this.state.heuristic.value;
    const movement = this.state.movementStyle.value;
    const t0 = performance.now();
    const visitedNodesInOrder = algorithm(
      this.grid,
      movement,
      startNode,
      finishNode,
      heuristic
    );
    const t1 = performance.now();
    console.log(`algorithm took ${t1 - t0} ms`);
    const nodesInShortestPathOrder = getNodesInShortestPathOrder(finishNode);
    this.animateAlgo(visitedNodesInOrder, nodesInShortestPathOrder);
  };

  toggleNodeObstacle = (row, col) => {
    const node = this.grid[row][col];
    if (node.type === "regular") node.type = "obstacle";
    else if (node.type === "obstacle") node.type = "regular";
    node.ref.current.toggleObstacle();
  };

  render() {
    const {
      animationInterval,
      algorithmType,
      heuristic,
      movementStyle,
      isAnimating,
    } = this.state;
    return (
      <React.Fragment>
        <ToastContainer
          position="top-center"
          hideProgressBar
          transition={Slide}
          autoClose={3000}
          closeButton={false}
          draggablePercent={20}
        />
        <GridRenderer
          grid={this.grid}
          handleMouseDown={this.handleMouseDown}
          handleMouseEnter={this.handleMouseEnter}
          handleMouseUp={this.handleMouseUp}
        />
        <ControlPanel
          isAnimating={isAnimating}
          handlers={{
            clearGrid: this.clearGrid,
            visualizeAlgo: this.visualizeAlgo,
            stopAnimation: this.stopAnimation,
            handleIntervalChange: this.handleIntervalChange,
            handleAlgorithmChange: this.handleAlgorithmChange,
            handleHeuristicChange: this.handleHeuristicChange,
            handleMovementStyleChange: this.handleMovementStyleChange,
            addRandomObstacles: this.addRandomObstacles,
            genMazeBinaryTree: this.genMazeBinaryTree,
            genMazeRandomPrims: this.genMazeRandomPrims,
          }}
          selectValues={{
            animationInterval,
            algorithmType,
            heuristic,
            movementStyle,
          }}
        />

      </React.Fragment>
    );
  }
}

export default Visualizer;
